import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChartLineUp, Users, Buildings, ClockCounterClockwise, DownloadSimple,
  CheckCircle, WarningCircle, Warning, MagnifyingGlass, CaretRight, Sparkle,
} from '@phosphor-icons/react';
import { HaleWordmark, HalePill } from '../components/HaleBrand';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =============================================================
 *  Hale · Admin Command Center (Phase H3)
 *  Reviewer load · Brand rollups · Usage timeline · Enterprise export
 *  Aesthetic: navy + Hale purple #E63946 + tabular precision
 * ===========================================================*/

const VERDICT_TONE = {
  no_concern:            { bg: 'bg-[#DEF7EC]', text: 'text-[#03543F]', label: 'No concern' },
  monitor:               { bg: 'bg-[#FEF3C7]', text: 'text-[#723B13]', label: 'Monitor' },
  further_review:        { bg: 'bg-[#E0F2FE]', text: 'text-[#075985]', label: 'Further review' },
  high_priority_concern: { bg: 'bg-[#FDE8E8]', text: 'text-[#9B1C1C]', label: 'High-priority' },
};

const STATUS_TONE = {
  intake:          'bg-gray-100 text-[#4B5563]',
  in_review:       'bg-[#E0F2FE] text-[#075985]',
  further_review:  'bg-[#FEF3C7] text-[#723B13]',
  concern:         'bg-[#FDE8E8] text-[#9B1C1C]',
  reviewed_pass:   'bg-[#DEF7EC] text-[#03543F]',
  certified:       'bg-[#FFE5E7] text-[#9B0014]',
  rejected:        'bg-[#FDE8E8] text-[#9B1C1C]',
  retired:         'bg-gray-200 text-[#4B5563]',
};

function KpiCard({ label, value, sub, color, icon: Icon }) {
  return (
    <div className="card-flat p-4 bg-white" data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">{label}</p>
        {Icon && <Icon size={14} weight="duotone" className="text-[#E63946]" />}
      </div>
      <p className={`text-2xl font-black tabular-nums ${color || 'text-[#111827]'}`}>{value ?? 0}</p>
      {sub && <p className="text-[10px] text-[#6B7280] mt-0.5">{sub}</p>}
    </div>
  );
}

function StatusMixBar({ mix, total }) {
  const entries = Object.entries(mix || {}).filter(([, v]) => v > 0);
  if (!entries.length || !total) return <span className="text-[10px] text-[#9CA3AF]">No SKUs</span>;
  return (
    <div className="flex h-2 rounded overflow-hidden">
      {entries.map(([k, v]) => (
        <div key={k} className={STATUS_TONE[k] || 'bg-gray-200'} style={{ width: `${v * 100 / total}%` }} title={`${k}: ${v}`} />
      ))}
    </div>
  );
}

function VerdictMixChips({ mix }) {
  const entries = Object.entries(mix || {}).filter(([, v]) => v > 0);
  if (!entries.length) return <span className="text-[10px] text-[#9CA3AF]">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => {
        const t = VERDICT_TONE[k] || { bg: 'bg-gray-100', text: 'text-[#4B5563]', label: k };
        return (
          <span key={k} className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${t.bg} ${t.text}`}>
            {t.label} <span className="tabular-nums">{v}</span>
          </span>
        );
      })}
    </div>
  );
}

function OverviewPanel({ data }) {
  if (!data) return <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p>;
  return (
    <div data-testid="overview-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <KpiCard label="Brands" value={data.brands?.total} sub={`${data.brands?.by_tier?.enterprise || 0} enterprise · ${data.brands?.by_tier?.emerging || 0} emerging`} icon={Buildings} />
        <KpiCard label="Total SKUs" value={data.skus?.total} icon={Sparkle} />
        <KpiCard label="Certified" value={data.skus?.by_status?.certified} color="text-[#9B0014]" sub="badge issued" />
        <KpiCard label="In review" value={data.skus?.by_status?.in_review} color="text-[#075985]" sub={`+ ${data.skus?.by_status?.further_review || 0} further · ${data.skus?.by_status?.concern || 0} concern`} />
        <KpiCard label="Awaiting reviewer" value={data.skus?.awaiting_assignment} color="text-[#FF4E6A]" />
        <KpiCard label="Verified eligible" value={data.verified?.eligible} sub={`of ${data.verified?.total || 0} reviews`} icon={CheckCircle} />
        <KpiCard label="Docs pending" value={data.verified?.documentation_pending} color="text-[#723B13]" sub="Verified queue" icon={Warning} />
      </div>

      {/* Verdict mix */}
      <div className="card-flat p-4 bg-white">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-3">Reviewed verdict mix (all-time)</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(VERDICT_TONE).map(([k, t]) => (
            <div key={k} className={`p-3 rounded ${t.bg}`}>
              <p className={`text-[10px] font-bold uppercase tracking-wider ${t.text}`}>{t.label}</p>
              <p className={`text-xl font-black tabular-nums ${t.text}`}>{data.skus?.verdict_mix?.[k] || 0}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ReviewersPanel({ data }) {
  if (!data) return <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p>;
  return (
    <div data-testid="reviewers-panel">
      <p className="text-[12px] text-[#4B5563] mb-3">
        <strong>{data.total_reviewers}</strong> reviewers — {data.loaded?.length || 0} loaded, {data.bench?.length || 0} on bench
      </p>
      <div className="card-flat bg-white overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-[#F3F4F6] text-[#4B5563]">
            <tr>
              <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">Reviewer</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Assigned</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Open</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">In review</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Further</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Concern</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Passed</th>
              <th className="text-right px-2 py-2 font-bold uppercase tracking-wider text-[10px]">Certified</th>
            </tr>
          </thead>
          <tbody>
            {(data.loaded || []).map(r => (
              <tr key={r.reviewer_id} className="border-t border-gray-100" data-testid={`reviewer-row-${r.reviewer_id}`}>
                <td className="px-3 py-2"><p className="font-semibold text-[#111827]">{r.name}</p><p className="text-[10px] text-[#6B7280]">{r.email || '—'}</p></td>
                <td className="px-2 py-2 text-right font-bold tabular-nums text-[#111827]">{r.total_assigned}</td>
                <td className="px-2 py-2 text-right font-bold tabular-nums text-[#FF4E6A]">{r.open_load}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#075985]">{r.in_review}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#723B13]">{r.further_review}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#9B1C1C]">{r.concern}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#03543F]">{r.reviewed_pass}</td>
                <td className="px-2 py-2 text-right tabular-nums text-[#9B0014]">{r.certified}</td>
              </tr>
            ))}
            {(data.bench || []).map(r => (
              <tr key={r.reviewer_id} className="border-t border-gray-100 bg-[#FAFAFA]" data-testid={`bench-row-${r.reviewer_id}`}>
                <td className="px-3 py-2"><p className="font-semibold text-[#4B5563]">{r.name} <span className="text-[9px] font-bold uppercase tracking-wider ml-1 bg-gray-100 px-1.5 py-0.5 rounded">bench</span></p><p className="text-[10px] text-[#9CA3AF]">{r.email || '—'}</p></td>
                <td className="px-2 py-2 text-right text-[#9CA3AF]">0</td>
                <td className="px-2 py-2 text-right text-[#9CA3AF]">0</td>
                <td colSpan={5} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BrandsPanel({ onBrandSelect }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [tier, setTier] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (tier) p.set('tier', tier);
      const r = await axios.get(`${API_URL}/api/hale/command/brands?${p}`, { withCredentials: true });
      setItems(r.data.items || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [q, tier]);

  useEffect(() => {
    const h = setTimeout(load, 250);
    return () => clearTimeout(h);
  }, [load]);

  return (
    <div data-testid="brands-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[200px] relative">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand / company / contact" className="h-9 text-sm pl-8" data-testid="brands-search" />
        </div>
        <select value={tier} onChange={(e) => setTier(e.target.value)} className="h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="brands-tier-filter">
          <option value="">All tiers</option>
          <option value="startup">Startup</option>
          <option value="emerging">Emerging</option>
          <option value="enterprise">Enterprise</option>
        </select>
      </div>
      {loading ? <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p> : items.length === 0 ? (
        <p className="text-sm text-[#4B5563] py-10 text-center">No brands match.</p>
      ) : (
        <div className="space-y-2">
          {items.map(b => (
            <button key={b.id} onClick={() => onBrandSelect(b.id)} className="w-full text-left card-flat p-3 bg-white hover:border-[#E63946] transition-colors" data-testid={`brand-card-${b.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#111827] truncate">{b.name}</p>
                  <p className="text-[11px] text-[#4B5563]">{b.company || '—'} · {b.country || '—'} · <span className="font-bold uppercase tracking-wider text-[10px] bg-[#FFE5E7] text-[#9B0014] px-1.5 py-0.5 rounded">{b.tier}</span></p>
                </div>
                <span className="text-[11px] font-bold tabular-nums text-[#111827]">{b.sku_count} SKUs</span>
              </div>
              <div className="mt-2">
                <StatusMixBar mix={b.status_mix} total={b.sku_count} />
              </div>
              <div className="flex items-center justify-between gap-2 mt-2">
                <VerdictMixChips mix={b.verdict_mix} />
                <p className="text-[10px] text-[#6B7280] tabular-nums whitespace-nowrap">
                  Verified {b.verified?.eligible || 0}/{b.verified?.total || 0}
                  {b.last_activity && <> · {new Date(b.last_activity).toLocaleDateString()}</>}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BrandDrillDown({ brandId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/hale/command/brands/${brandId}`, { withCredentials: true });
        setData(r.data);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [brandId]);

  if (loading || !data) return <div className="card-flat p-6 text-sm text-[#4B5563]">Loading brand dashboard…</div>;

  return (
    <div data-testid="brand-drilldown">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563]">Brand · {data.brand.tier}</p>
          <h2 className="text-xl font-bold text-[#111827]">{data.brand.name}</h2>
          <p className="text-[11px] text-[#4B5563]">{data.brand.company || '—'} · {data.brand.contact_email} · {data.brand.country || '—'}</p>
        </div>
        <Button onClick={onClose} variant="outline" className="h-8 text-xs rounded" data-testid="drilldown-close">← Back to brands</Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        <KpiCard label="SKUs" value={data.rollup.sku_count} />
        <KpiCard label="Certified" value={data.rollup.status_mix?.certified || 0} color="text-[#9B0014]" />
        <KpiCard label="In review" value={(data.rollup.status_mix?.in_review || 0) + (data.rollup.status_mix?.further_review || 0) + (data.rollup.status_mix?.concern || 0)} color="text-[#FF4E6A]" />
        <KpiCard label="Verified reviews" value={data.rollup.verified_count} />
        <KpiCard label="Verified eligible" value={data.rollup.verified_eligible_count} color="text-[#03543F]" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-flat bg-white">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Reviewed SKUs ({data.skus.length})</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100">
            {data.skus.map(s => {
              const v = s.verdict?.key;
              const t = VERDICT_TONE[v];
              return (
                <div key={s.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`brand-sku-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#111827] truncate">{s.product_name}</p>
                    <p className="text-[10px] text-[#6B7280]">{s.reviewer_name || 'unassigned'} · {s.ingredient_count} ing</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${STATUS_TONE[s.status] || 'bg-gray-100'}`}>{s.status}</span>
                    {t && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${t.bg} ${t.text}`}>{t.label}</span>}
                  </div>
                </div>
              );
            })}
            {data.skus.length === 0 && <p className="text-sm text-[#4B5563] py-6 text-center">No SKUs yet.</p>}
          </div>
        </div>
        <div className="card-flat bg-white">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Verified reviews ({data.verified.length})</p>
          </div>
          <div className="max-h-[400px] overflow-y-auto divide-y divide-gray-100">
            {data.verified.map(v => (
              <div key={v.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`brand-verified-${v.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-[#111827]">{v.sku_id}</p>
                  <p className="text-[10px] text-[#6B7280] tabular-nums">{v.eligibility?.weighted_score_pct ?? 0}% weighted · {v.eligibility?.filled}/{v.eligibility?.total} docs</p>
                </div>
                <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${v.eligibility?.badge_eligible ? 'bg-[#DEF7EC] text-[#03543F]' : 'bg-[#FEF3C7] text-[#723B13]'}`}>
                  {v.eligibility?.verdict || v.status}
                </span>
              </div>
            ))}
            {data.verified.length === 0 && <p className="text-sm text-[#4B5563] py-6 text-center">No Verified reviews yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function TimelinePanel() {
  const [weeks, setWeeks] = useState([]);
  const [n, setN] = useState(12);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/hale/command/timeline?weeks=${n}`, { withCredentials: true });
        setWeeks(r.data.weeks || []);
      } catch { /* noop */ }
      finally { setLoading(false); }
    })();
  }, [n]);

  const maxTotal = Math.max(1, ...weeks.map(w => w.total));

  return (
    <div data-testid="timeline-panel">
      <div className="flex items-center gap-2 mb-4">
        <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Window</Label>
        <select value={n} onChange={(e) => setN(Number(e.target.value))} className="h-8 text-xs border border-gray-300 rounded px-2 bg-white" data-testid="timeline-weeks">
          {[4, 8, 12, 24, 52].map(w => <option key={w} value={w}>{w} weeks</option>)}
        </select>
      </div>
      {loading ? <p className="text-sm text-[#4B5563] py-10 text-center">Loading…</p> : weeks.length === 0 ? (
        <p className="text-sm text-[#4B5563] py-10 text-center">No activity in this window.</p>
      ) : (
        <div className="card-flat p-4 bg-white">
          <div className="space-y-1.5">
            {weeks.map(w => (
              <div key={w.week} className="grid grid-cols-[80px_1fr_60px] items-center gap-2 text-[11px]" data-testid={`timeline-row-${w.week}`}>
                <p className="font-mono text-[#4B5563]">{w.week}</p>
                <div className="flex h-3 rounded overflow-hidden bg-gray-100">
                  {w.no_concern > 0 && <div className="bg-[#31C48D]" style={{ width: `${w.no_concern * 100 / maxTotal}%` }} title={`No concern ${w.no_concern}`} />}
                  {w.monitor > 0 && <div className="bg-[#F3CC64]" style={{ width: `${w.monitor * 100 / maxTotal}%` }} title={`Monitor ${w.monitor}`} />}
                  {w.further_review > 0 && <div className="bg-[#7DD3FC]" style={{ width: `${w.further_review * 100 / maxTotal}%` }} title={`Further review ${w.further_review}`} />}
                  {w.high_priority_concern > 0 && <div className="bg-[#F8B4B4]" style={{ width: `${w.high_priority_concern * 100 / maxTotal}%` }} title={`High-priority ${w.high_priority_concern}`} />}
                </div>
                <p className="text-right font-bold tabular-nums text-[#111827]">{w.total}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100 text-[10px] text-[#4B5563]">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#31C48D]" /> No concern</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#F3CC64]" /> Monitor</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#7DD3FC]" /> Further</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#F8B4B4]" /> High-priority</span>
          </div>
        </div>
      )}
    </div>
  );
}

function ExportPanel() {
  const [rows, setRows] = useState([]);
  const [tier, setTier] = useState('');
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (tier) p.set('tier', tier);
      const r = await axios.get(`${API_URL}/api/hale/command/enterprise-export?${p}`, { withCredentials: true });
      setRows(r.data.rows || []);
      toast.success(`Loaded ${r.data.rows.length} rows`);
    } catch { toast.error('Failed to load export'); }
    finally { setLoading(false); }
  };

  const csv = () => {
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const lines = [headers.join(',')].concat(rows.map(r => headers.map(h => {
      const v = r[h];
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    }).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `hale_enterprise_export_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="export-panel">
      <div className="card-flat p-5 bg-white mb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4B5563] mb-2">Enterprise BI export</p>
        <p className="text-[12px] text-[#4B5563] mb-3">Flat brand × SKU × verdict × Verified-eligibility rows. CSV-ready. Use for retailer reporting, board decks, or downstream BI.</p>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <Label className="text-[11px] font-bold uppercase tracking-wider text-[#4B5563]">Tier</Label>
            <select value={tier} onChange={(e) => setTier(e.target.value)} className="mt-1 h-9 text-sm border border-gray-300 rounded px-2 bg-white" data-testid="export-tier">
              <option value="">All</option><option value="startup">Startup</option><option value="emerging">Emerging</option><option value="enterprise">Enterprise</option>
            </select>
          </div>
          <Button onClick={load} disabled={loading} className="h-9 rounded bg-[#E63946] hover:bg-[#C9192C] text-white text-sm font-semibold" data-testid="export-load">
            {loading ? 'Loading…' : 'Load preview'}
          </Button>
          <Button onClick={csv} disabled={!rows.length} className="h-9 rounded bg-[#0B1426] hover:bg-[#1B2538] text-white text-sm font-semibold" data-testid="export-csv">
            <DownloadSimple size={13} weight="bold" className="mr-1.5" /> Download CSV ({rows.length})
          </Button>
        </div>
      </div>
      {rows.length > 0 && (
        <div className="card-flat bg-white overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="bg-[#F3F4F6] text-[#4B5563]">
              <tr>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Brand</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Tier</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">SKU</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Status</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Verdict</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Reviewer</th>
                <th className="text-left px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Verified</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 200).map((r, i) => (
                <tr key={i} className="border-t border-gray-100" data-testid={`export-row-${i}`}>
                  <td className="px-2 py-1.5 font-semibold">{r.brand_name}</td>
                  <td className="px-2 py-1.5 uppercase tracking-wider text-[9px] text-[#9B0014]">{r.brand_tier}</td>
                  <td className="px-2 py-1.5">{r.product_name}</td>
                  <td className="px-2 py-1.5"><span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${STATUS_TONE[r.reviewed_status] || 'bg-gray-100'}`}>{r.reviewed_status || '—'}</span></td>
                  <td className="px-2 py-1.5">{r.reviewed_verdict_label || '—'}</td>
                  <td className="px-2 py-1.5">{r.reviewer_name || '—'}</td>
                  <td className="px-2 py-1.5">{r.verified_badge_eligible ? <span className="text-[#03543F] font-bold">eligible</span> : <span className="text-[#4B5563]">{r.verified_status || '—'}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 200 && <p className="text-[10px] text-[#9CA3AF] p-2">Showing first 200 of {rows.length} rows — CSV download includes all.</p>}
        </div>
      )}
    </div>
  );
}

export default function HaleCommandPage() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [reviewers, setReviewers] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, r] = await Promise.all([
          axios.get(`${API_URL}/api/hale/command/overview`, { withCredentials: true }),
          axios.get(`${API_URL}/api/hale/command/reviewers`, { withCredentials: true }),
        ]);
        setOverview(o.data);
        setReviewers(r.data);
      } catch { /* noop */ }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <HaleWordmark size="md" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0B0B0B] bg-[#FFE5E7] border border-[#E63946]/40 px-2 py-0.5 rounded">Command Center · Phase H3</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <ChartLineUp size={22} className="text-[#E63946]" weight="duotone" />
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]">Hale · Command Center</h1>
          </div>
          <p className="text-sm text-[#4B5563]">
            Cross-brand programme command for the Hale partnership — reviewer load, brand-level rollups, weekly verdict throughput, and enterprise BI export.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Link to="/assessor"><Button variant="outline" className="h-8 text-xs rounded">← Assessor Console</Button></Link>
            <Link to="/assessor/hale"><Button variant="outline" className="h-8 text-xs rounded" data-testid="command-to-reviewed">Hale Reviewed →</Button></Link>
            <Link to="/assessor/hale-verified"><Button variant="outline" className="h-8 text-xs rounded" data-testid="command-to-verified">Hale Verified →</Button></Link>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 mb-5 border-b border-gray-200">
          {[
            { k: 'overview', label: 'Overview', icon: Sparkle },
            { k: 'reviewers', label: 'Reviewer load', icon: Users },
            { k: 'brands', label: 'Brand dashboards', icon: Buildings },
            { k: 'timeline', label: 'Timeline', icon: ClockCounterClockwise },
            { k: 'export', label: 'Enterprise export', icon: DownloadSimple },
          ].map(t => (
            <button key={t.k} onClick={() => { setTab(t.k); setSelectedBrand(null); }}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.k ? 'border-[#E63946] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'}`}
              data-testid={`command-tab-${t.k}`}>
              <t.icon size={14} weight="duotone" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewPanel data={overview} />}
        {tab === 'reviewers' && <ReviewersPanel data={reviewers} />}
        {tab === 'brands' && (selectedBrand ? (
          <BrandDrillDown brandId={selectedBrand} onClose={() => setSelectedBrand(null)} />
        ) : (
          <BrandsPanel onBrandSelect={setSelectedBrand} />
        ))}
        {tab === 'timeline' && <TimelinePanel />}
        {tab === 'export' && <ExportPanel />}
      </main>
    </div>
  );
}
